import React, { useState, useRef, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import { CalendarMonth } from '@mui/icons-material'
import 'react-datepicker/dist/react-datepicker.css'

export default function CustomDateRangePicker({ startDate, endDate, handleDateChange }) {
	const [isOpen, setIsOpen] = useState(false)
	const [localStartDate, setLocalStartDate] = useState(startDate ? new Date(startDate) : null)
	const [localEndDate, setLocalEndDate] = useState(endDate ? new Date(endDate) : null)
	const datePickerRef = useRef(null)
	const containerRef = useRef(null)
	const inputRef = useRef(null)

	useEffect(() => {
		setLocalStartDate(startDate ? new Date(startDate) : null)
		setLocalEndDate(endDate ? new Date(endDate) : null)
	}, [startDate, endDate])

	const handleCalendarOpen = () => {
		setIsOpen(true)
		if (datePickerRef.current) {
			datePickerRef.current.setOpen(true)
		}
	}

	const handleCalendarClose = () => {
		setIsOpen(false)
	}

	const handleContainerClick = (e) => {
		e.preventDefault()
		e.stopPropagation()

		// Prevent mobile keyboard
		if (inputRef.current) {
			const input = inputRef.current.input
			if (input) {
				input.setAttribute('readonly', true)
				input.blur()
			}
		}

		// Open calendar
		handleCalendarOpen()
	}

	const normalizeDate = (date) => {
		if (date && !isNaN(date)) {
			const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
			return normalizedDate
		}
		return null
	}

	const handleDateChangeInternal = (dates) => {
		const [start, end] = dates
		const normalizedStart = normalizeDate(start)
		const normalizedEnd = normalizeDate(end)

		setLocalStartDate(normalizedStart)
		setLocalEndDate(normalizedEnd)

		// Prevent mobile keyboard after date selection
		if (inputRef.current) {
			const input = inputRef.current.input
			if (input) {
				input.setAttribute('readonly', true)
				input.blur()
			}
		}

		handleDateChange({
			startDate: normalizedStart ? normalizedStart.toISOString() : null,
			endDate: normalizedEnd ? normalizedEnd.toISOString() : null,
		})
	}

	return (
		<div ref={containerRef} className='input-group input-group-outline w-100' aria-label='Date range picker' onClick={handleContainerClick}>
			<div style={{ position: 'relative', right: 0, width: '100%' }}>
				<DatePicker
					ref={(r) => {
						datePickerRef.current = r
						inputRef.current = r
					}}
					selected={localStartDate}
					onChange={handleDateChangeInternal}
					startDate={localStartDate}
					endDate={localEndDate}
					selectsRange
					dateFormat='dd/MM/yyyy'
					placeholderText='Select Date Range'
					className='form-control'
					maxDate={new Date()}
					onCalendarOpen={handleCalendarOpen}
					onCalendarClose={handleCalendarClose}
					open={isOpen}
					popperProps={{
						strategy: 'fixed',
						modifiers: [
							{
								name: 'flip',
								enabled: true,
							},
							{
								name: 'preventOverflow',
								options: {
									boundary: 'viewport',
									padding: 10,
								},
							},
						],
					}}
					popperPlacement='bottom-start'
					// Prevent default input behavior on mobile
					onFocus={(e) => {
						e.preventDefault()
						const input = e.target
						input.setAttribute('readonly', true)
						input.blur()
					}}
				/>
				<button
					type='button'
					onClick={handleContainerClick}
					style={{
						position: 'absolute',
						right: 0,
						top: '50%',
						transform: 'translateY(-50%)',
						background: 'none',
						border: 'none',
						cursor: 'pointer',
					}}
					aria-label='Open calendar'
				>
					<CalendarMonth />
				</button>
			</div>
		</div>
	)
}
